import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { Download, Minus, Plus, Settings, Upload } from "lucide-react";
import { API, emptyForm, fmtErr, money } from "../lib/pos";

export function MenuBuilder({ menu, categories, refresh }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm(categories));
  const [editing, setEditing] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const bulkInputRef = useRef(null);

  const filteredMenu = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (menu || []).filter((m) => {
      if (filterCategory !== "All" && m.category !== filterCategory) return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        (m.description || "").toLowerCase().includes(q) ||
        (m.category || "").toLowerCase().includes(q)
      );
    });
  }, [menu, search, filterCategory]);

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        category: form.category,
        description: form.description,
        price: Number(form.price) || 0,
        variants: form.variants.map((v) => ({
          id: v.id,
          name: v.name,
          price_delta: Number(v.price_delta) || 0,
          sub_variants: (v.sub_variants || []).map((sv) => ({
            id: sv.id,
            name: sv.name,
            price_delta: Number(sv.price_delta) || 0,
          })).filter((sv) => sv.name.trim()),
        })).filter((v) => v.name.trim()),
        addons: form.addons.map((a) => ({
          id: a.id,
          name: a.name,
          price: Number(a.price) || 0,
          variant_ids: a.variant_ids || [],
        })).filter((a) => a.name.trim()),
      };
      if (editing) await axios.patch(`${API}/menu/${editing}`, payload);
      else await axios.post(`${API}/menu`, payload);
      setShow(false); setEditing(null); setForm(emptyForm(categories));
      refresh();
    } catch (e) { alert(fmtErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    await axios.delete(`${API}/menu/${id}`); refresh();
  };
  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      name: item.name,
      category: item.category,
      price: String(item.price ?? ""),
      description: item.description || "",
      variants: (item.variants || []).map((v) => ({
        id: v.id,
        name: v.name,
        price_delta: String(v.price_delta ?? 0),
        sub_variants: (v.sub_variants || []).map((sv) => ({ id: sv.id, name: sv.name, price_delta: String(sv.price_delta ?? 0) })),
      })),
      addons: (item.addons || []).map((a) => ({ id: a.id, name: a.name, price: String(a.price ?? 0), variant_ids: a.variant_ids || [] })),
    });
    setShow(true);
  };
  const openNew = () => { setEditing(null); setForm(emptyForm(categories)); setShow(true); };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { id: `v_${Math.random().toString(36).slice(2, 8)}`, name: "", price_delta: "0", sub_variants: [] }] }));
  const patchVariant = (i, patch) => setForm((f) => ({ ...f, variants: f.variants.map((v, ix) => (ix === i ? { ...v, ...patch } : v)) }));
  const removeVariant = (i) => setForm((f) => {
    const removed = f.variants[i];
    return {
      ...f,
      variants: f.variants.filter((_, ix) => ix !== i),
      // scrub the removed variant from any addon's variant_ids
      addons: f.addons.map((a) => ({ ...a, variant_ids: (a.variant_ids || []).filter((id) => id !== removed.id) })),
    };
  });
  const addSubVariant = (vi) => setForm((f) => ({
    ...f,
    variants: f.variants.map((v, ix) => (ix === vi
      ? { ...v, sub_variants: [...(v.sub_variants || []), { id: `sv_${Math.random().toString(36).slice(2, 8)}`, name: "", price_delta: "0" }] }
      : v)),
  }));
  const patchSubVariant = (vi, si, patch) => setForm((f) => ({
    ...f,
    variants: f.variants.map((v, ix) => (ix === vi
      ? { ...v, sub_variants: (v.sub_variants || []).map((sv, sx) => (sx === si ? { ...sv, ...patch } : sv)) }
      : v)),
  }));
  const removeSubVariant = (vi, si) => setForm((f) => ({
    ...f,
    variants: f.variants.map((v, ix) => (ix === vi
      ? { ...v, sub_variants: (v.sub_variants || []).filter((_, sx) => sx !== si) }
      : v)),
  }));
  const addAddon = () => setForm((f) => ({ ...f, addons: [...f.addons, { id: `a_${Math.random().toString(36).slice(2, 8)}`, name: "", price: "0", variant_ids: [] }] }));
  const patchAddon = (i, patch) => setForm((f) => ({ ...f, addons: f.addons.map((a, ix) => (ix === i ? { ...a, ...patch } : a)) }));
  const removeAddon = (i) => setForm((f) => ({ ...f, addons: f.addons.filter((_, ix) => ix !== i) }));
  const toggleAddonVariant = (i, vid) => setForm((f) => ({
    ...f,
    addons: f.addons.map((a, ix) => {
      if (ix !== i) return a;
      const has = (a.variant_ids || []).includes(vid);
      return { ...a, variant_ids: has ? a.variant_ids.filter((x) => x !== vid) : [...(a.variant_ids || []), vid] };
    }),
  }));

  const summariseItem = (x) => {
    const bits = [];
    if ((x.variants || []).length) bits.push(`${x.variants.length} variant${x.variants.length > 1 ? "s" : ""}`);
    if ((x.addons || []).length) bits.push(`${x.addons.length} add-on${x.addons.length > 1 ? "s" : ""}`);
    return bits.join(" · ");
  };

  const doBulkUpload = async (file) => {
    if (!file) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/menu/bulk-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(r.data);
      refresh();
    } catch (e) { alert(fmtErr(e)); }
    finally { setBulkBusy(false); if (bulkInputRef.current) bulkInputRef.current.value = ""; }
  };
  const onDrop = (e) => {
    e.preventDefault(); setDropActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) doBulkUpload(f);
  };
  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/menu/bulk-template.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "menu-bulk-template.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) { alert(fmtErr(e)); }
  };

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER TOOLS / CATALOG</p>
          <h1>Menu builder</h1>
          <p className="muted">Item category routes tickets to stations. Add variants (Half/Full, crusts, sizes) and add-ons per variant or independent.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button data-testid="download-template-button" className="outline" onClick={downloadTemplate} title="CSV template with sample rows">
            <Download size={14} />Template
          </button>
          <button data-testid="bulk-upload-button" className="outline" onClick={() => bulkInputRef.current?.click()}>
            <Upload size={14} />{bulkBusy ? "Uploading…" : "Bulk upload"}
          </button>
          <input
            data-testid="bulk-upload-file-input"
            ref={bulkInputRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: "none" }}
            onChange={(e) => doBulkUpload(e.target.files?.[0])}
          />
          <button data-testid="add-menu-item-button" className="primary" onClick={openNew}>
            <Plus size={17} />Add item
          </button>
        </div>
      </div>

      <div
        className={`bulk-drop ${dropActive ? "active" : ""} ${bulkBusy ? "busy" : ""}`}
        data-testid="bulk-drop-zone"
        onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
        onDragLeave={() => setDropActive(false)}
        onDrop={onDrop}
        onClick={() => !bulkBusy && bulkInputRef.current?.click()}
      >
        <Upload size={22} />
        <b>{bulkBusy ? "Uploading & inserting…" : "Drop a CSV or Excel file here to bulk-add or update items"}</b>
        <small>Existing names are updated in place · missing categories auto-created · station column routes each category · variants & addons accept JSON list</small>
      </div>
      <div className="menu-filter-bar" data-testid="menu-filter-bar">
        <div className="menu-search" style={{ flex: 1, minWidth: 240 }}>
          <input
            data-testid="menu-builder-search-input"
            type="search"
            placeholder="Search items by name, description or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button data-testid="menu-builder-search-clear" className="search-clear" onClick={() => setSearch("")}>×</button>
          )}
        </div>
        <select
          data-testid="menu-builder-category-filter"
          className="filter-select"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="All">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="muted" data-testid="menu-filter-count" style={{ fontSize: 11 }}>
          Showing <b style={{ color: "#fff" }}>{filteredMenu.length}</b> of {menu.length}
        </span>
      </div>
      <div className="menu-table">
        <div className="table-head"><span>ITEM</span><span>CATEGORY</span><span>PRICE</span><span>ACTIONS</span></div>
        {filteredMenu.length === 0 && (
          <div className="empty" data-testid="menu-builder-no-results" style={{ minHeight: 160 }}>
            <Settings size={26} />
            <b>No items match{search ? ` “${search}”` : ""}{filterCategory !== "All" ? ` in ${filterCategory}` : ""}</b>
            <span>Adjust the search or category filter above</span>
          </div>
        )}
        {filteredMenu.map((x) => {
          const isOff = x.active === false;
          return (
            <div className={`menu-line ${isOff ? "menu-off" : ""}`} key={x.id}>
              <div>
                <b>{x.name}{isOff && <small className="off-tag"> DISABLED</small>}</b>
                <small>{x.description}{summariseItem(x) && ` · ${summariseItem(x)}`}</small>
              </div>
              <span>{x.category}</span>
              <strong>{money(x.price)}</strong>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button
                  data-testid={`menu-${x.id}-active-toggle`}
                  className={`toggle ${isOff ? "off" : "on"}`}
                  onClick={async () => {
                    try { await axios.patch(`${API}/menu/${x.id}`, { active: !(!isOff) }); refresh(); }
                    catch (e) { alert(fmtErr(e)); }
                  }}
                  title={isOff ? "Enable" : "Disable"}
                ><i /><span>{isOff ? "OFF" : "ON"}</span></button>
                <button data-testid={`edit-menu-${x.id}-button`} className="outline" onClick={() => openEdit(x)}>Edit</button>
                <button data-testid={`delete-menu-${x.id}-button`} className="outline" onClick={() => remove(x.id)}>Delete</button>
              </div>
            </div>
          );
        })}
      </div>
      {show && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setShow(false)}>
          <div className="modal builder" style={{ width: "min(640px, 94vw)", maxHeight: "88vh", overflowY: "auto" }}>
            <div className="section-line">
              <h2>{editing ? "Edit item" : "New menu item"}</h2>
              <button data-testid="close-menu-modal-button" onClick={() => setShow(false)}>×</button>
            </div>
            <label className="field">Name<input data-testid="menu-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="field">Description<input data-testid="menu-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label className="field">Base price<input data-testid="menu-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></label>
              <label className="field">Category
                <select data-testid="menu-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>

            <div className="section-line" style={{ marginTop: 18 }}>
              <p className="eyebrow" style={{ margin: 0 }}>VARIANTS <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· added to base price</span></p>
              <button data-testid="add-variant-button" className="outline" onClick={addVariant}><Plus size={12} style={{ marginRight: 4 }} />Variant</button>
            </div>
            {form.variants.length === 0 && <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>No variants — item sells at base price.</p>}
            {form.variants.map((v, i) => (
              <div className="variant-editor" key={v.id || i}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
                  <input data-testid={`variant-name-input-${i}`} placeholder="Name (Half / Thin crust / Large)" value={v.name} onChange={(e) => patchVariant(i, { name: e.target.value })} />
                  <input data-testid={`variant-delta-input-${i}`} placeholder="+₹" value={v.price_delta} onChange={(e) => patchVariant(i, { price_delta: e.target.value })} style={{ maxWidth: 90 }} />
                  <button data-testid={`variant-remove-${i}-button`} className="icon-btn" onClick={() => removeVariant(i)}><Minus size={13} /></button>
                </div>
                <div className="sub-variant-editor" data-testid={`sub-variant-editor-${i}`}>
                  <div className="sub-variant-head">
                    <small>SUB-VARIANTS · multi-select once, added to price</small>
                    <button data-testid={`add-sub-variant-${i}-button`} className="outline sm" onClick={() => addSubVariant(i)}><Plus size={11} style={{ marginRight: 3 }} />Sub-variant</button>
                  </div>
                  {(v.sub_variants || []).length === 0 && (
                    <p className="muted" style={{ fontSize: 10, margin: "2px 0 0" }}>No sub-variants — pick this variant to add it at its own price.</p>
                  )}
                  {(v.sub_variants || []).map((sv, si) => (
                    <div className="sv-row" key={sv.id || si}>
                      <input data-testid={`sub-variant-name-${i}-${si}`} placeholder="Name (Cheese burst / Thin crust)" value={sv.name} onChange={(e) => patchSubVariant(i, si, { name: e.target.value })} />
                      <input data-testid={`sub-variant-delta-${i}-${si}`} placeholder="+₹" value={sv.price_delta} onChange={(e) => patchSubVariant(i, si, { price_delta: e.target.value })} style={{ maxWidth: 80 }} />
                      <button data-testid={`sub-variant-remove-${i}-${si}`} className="icon-btn" onClick={() => removeSubVariant(i, si)}><Minus size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="section-line" style={{ marginTop: 18 }}>
              <p className="eyebrow" style={{ margin: 0 }}>ADD-ONS <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· pick specific variants, or leave empty for independent</span></p>
              <button data-testid="add-addon-button" className="outline" onClick={addAddon}><Plus size={12} style={{ marginRight: 4 }} />Add-on</button>
            </div>
            {form.addons.length === 0 && <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>No add-ons.</p>}
            {form.addons.map((a, i) => (
              <div className="addon-editor" key={a.id || i}>
                <div className="ae-row">
                  <input data-testid={`addon-name-input-${i}`} placeholder="Name (Extra cheese)" value={a.name} onChange={(e) => patchAddon(i, { name: e.target.value })} />
                  <input data-testid={`addon-price-input-${i}`} placeholder="₹" value={a.price} onChange={(e) => patchAddon(i, { price: e.target.value })} />
                  <button data-testid={`addon-remove-${i}-button`} className="icon-btn" onClick={() => removeAddon(i)}><Minus size={13} /></button>
                </div>
                {form.variants.length > 0 && (
                  <div className="ae-variants">
                    <small>Offer with:</small>
                    <button data-testid={`addon-${i}-all-button`}
                      className={`chip ${((a.variant_ids || []).length === 0) ? "chip-on" : ""}`}
                      onClick={() => patchAddon(i, { variant_ids: [] })}>Any / independent</button>
                    {form.variants.map((v) => {
                      const on = (a.variant_ids || []).includes(v.id);
                      return (
                        <button data-testid={`addon-${i}-variant-${v.id}-button`} key={v.id}
                          className={`chip ${on ? "chip-on" : ""}`}
                          onClick={() => toggleAddonVariant(i, v.id)}>{v.name || "(unnamed)"}</button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            <button data-testid="save-menu-item-button" className="primary wide" style={{ marginTop: 18 }} onClick={save}>Save menu item</button>
          </div>
        </div>
      )}

      {bulkResult && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setBulkResult(null)}>
          <div className="modal" style={{ width: "min(560px, 94vw)", maxHeight: "82vh", overflowY: "auto" }} data-testid="bulk-result-modal">
            <div className="section-line">
              <h2>Bulk upload result</h2>
              <button data-testid="close-bulk-result-button" onClick={() => setBulkResult(null)}>×</button>
            </div>
            <div className="bulk-summary">
              <div><span>Added</span><b data-testid="bulk-created-count" className="green">{bulkResult.created}</b></div>
              <div><span>Updated</span><b data-testid="bulk-updated-count" className="green">{bulkResult.updated || 0}</b></div>
              <div><span>Errors</span><b data-testid="bulk-error-count" className={bulkResult.errors?.length ? "red" : ""}>{bulkResult.errors?.length || 0}</b></div>
            </div>
            {(bulkResult.rows_updated?.length || 0) > 0 && (
              <div className="bulk-block">
                <p className="eyebrow">UPDATED IN PLACE</p>
                <ul className="bulk-list">
                  {bulkResult.rows_updated.map((r) => <li key={r.id}><b>{r.name}</b> · {r.category} · {money(r.price)}</li>)}
                </ul>
              </div>
            )}
            {bulkResult.created_categories?.length > 0 && (
              <div className="bulk-block">
                <p className="eyebrow">NEW CATEGORIES CREATED</p>
                <div className="cat-chips">
                  {bulkResult.created_categories.map((c) => <span className="chip chip-on" key={c}>{c}</span>)}
                </div>
              </div>
            )}
            {bulkResult.station_assignments?.length > 0 && (
              <div className="bulk-block">
                <p className="eyebrow">STATION ROUTING APPLIED</p>
                <ul className="bulk-list">
                  {bulkResult.station_assignments.map((a, i) => (
                    <li key={i}><b>{a.category}</b> → {a.station}</li>
                  ))}
                </ul>
              </div>
            )}
            {bulkResult.duplicates?.length > 0 && (
              <div className="bulk-block">
                <p className="eyebrow">DUPLICATES SKIPPED</p>
                <ul className="bulk-list">
                  {bulkResult.duplicates.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}
            {bulkResult.errors?.length > 0 && (
              <div className="bulk-block">
                <p className="eyebrow" style={{ color: "var(--red)" }}>ERRORS</p>
                <ul className="bulk-list bulk-errors">
                  {bulkResult.errors.map((er, i) => (
                    <li key={i}><b>Row {er.row}</b>{er.name ? ` · ${er.name}` : ""} — {er.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <button data-testid="bulk-dismiss-button" className="primary wide" style={{ marginTop: 14 }} onClick={() => setBulkResult(null)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Categories Admin (manager) ---------- */
